import { useState, useEffect, useCallback, useMemo } from "react";
import { IMPORT_STRATEGIES } from "../importStrategies";

export const useImportValidation = (initialData, dataType, contextData, loading) => {
  // This hook manages the Grid Data (replacing 'modifiedData' in your old component)
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [hasBlockingErrors, setHasBlockingErrors] = useState(false);

  // 1. INITIAL LOAD: When data arrives from dependencies, set up the rows
  useEffect(() => {
    if (!loading && initialData && rows.length === 0) {
      // Ensure every row has an ID for the DataGrid
      const indexedData = initialData.map((row, index) => ({
        id: index,
        ...row,
      }));
      setRows(indexedData);
      // Run initial validation
      runFullValidation(indexedData);
    }
  }, [loading, initialData]);

  // 2. THE VALIDATION ENGINE
  const runFullValidation = useCallback(
    (currentRows) => {
      const strategy = IMPORT_STRATEGIES[dataType];
      if (!strategy) return;

      const newErrors = {};
      const trackers = strategy.initTrackers ? strategy.initTrackers() : {};
      let blocking = false;

      currentRows.forEach((row) => {
        const rowErrs = {};

        // A. Check Required Fields
        (strategy.requiredFields || []).forEach((field) => {
          if (!row[field] || String(row[field]).trim() === "") {
            rowErrs[field] = `${field} is required`;
          }
        });

        // B. Run Strategy Logic
        if (strategy.validateRow) {
          const specificErrors = strategy.validateRow(row, contextData, trackers, currentRows);
          // Map array of error strings to a generic field if specific field isn't known,
          // or just use the first error for display purposes.
          if (specificErrors.length > 0) {
            // If your validateRow returns ["Duplicate ID"], we can attach it to a generic key
            // or map it to specific fields if you update validateRow to return objects.
            // For now, we store it generally or attach to the first column.
            rowErrs["_general"] = specificErrors[0];
          }
        }
        if (Object.keys(rowErrs).length > 0) {
          newErrors[row.id] = rowErrs;
          blocking = true;
        }
      });

      setErrors(newErrors);
      setHasBlockingErrors(blocking);
    },
    [dataType, contextData]
  );

  // 3. CRUD HANDLERS (The missing pieces)

  // --- DELETE ROW ---
  const handleDeleteRow = (id) => {
    const updatedRows = rows.filter((r) => r.id !== id);
    setRows(updatedRows);
    runFullValidation(updatedRows);
  };

  // --- ADD ROW ---
  const handleAddRow = () => {
    // Generate a new ID (highest existing ID + 1)
    const newId = rows.length > 0 ? Math.max(...rows.map((r) => r.id)) + 1 : 0;
    const newRow = { id: newId };

    const updatedRows = [...rows, newRow];
    setRows(updatedRows);
    runFullValidation(updatedRows);
  };

  // --- UPDATE ROW (Called by DataGrid processRowUpdate) ---
  const processRowUpdate = (newRow) => {
    const updatedRows = rows.map((r) => (r.id === newRow.id ? newRow : r));
    setRows(updatedRows);
    runFullValidation(updatedRows);
    return newRow;
  };

  // 4. GET COLUMNS
  // We re-calculate columns when contextData changes (so dropdowns populate)
  // We pass 'rows' so the strategy can find newly added items (like new Jobs)
  const columns = useMemo(() => {
    const strategy = IMPORT_STRATEGIES[dataType];
    return strategy ? strategy.getColumns(contextData, rows) : [];
  }, [dataType, contextData, rows]);

  return {
    rows,
    columns,
    errors,
    hasBlockingErrors,
    // Export the handlers so the UI can use them
    handleAddRow,
    handleDeleteRow,
    processRowUpdate,
  };
};
